import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { ContractTermsResponse } from "@bw/shared/types/generated";

import { SectionTitle } from "./LaunchReadiness";
import {
  describeAcceptedTerms,
  describeCommercialState,
  formatCommissionRate,
  formatOptionalDate,
} from "./terms";

function AgreementFact({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <Box
      component="div"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "10rem minmax(0, 1fr)" },
        gap: 1.25,
        py: 1.25,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        {detail && (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
            {detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function CommercialAgreement({
  terms,
}: {
  terms: ContractTermsResponse | null;
}) {
  return (
    <Stack component="section" spacing={1.5}>
      <SectionTitle
        title="Commercial agreement"
        detail="Accepted commercial terms supplied by the contracts service."
      />
      {!terms ? (
        <Alert severity="warning">
          The business has not accepted commercial terms yet.
        </Alert>
      ) : (
        <Box component="dl" sx={{ m: 0 }}>
          <AgreementFact
            label="Accepted terms"
            value={describeAcceptedTerms(terms)}
            detail={`Effective: ${formatOptionalDate(terms.effective_date)}. Commission: ${formatCommissionRate(terms)}. Renewal: ${
              terms.termination_date
                ? formatOptionalDate(terms.termination_date)
                : "No end date"
            }.`}
          />
          {terms.upcoming_version_number != null && (
            <AgreementFact
              label="Scheduled change"
              value={`v${terms.upcoming_version_number} takes effect ${formatOptionalDate(terms.upcoming_effective_date)}`}
              detail={`Current commercial status: ${describeCommercialState(terms)}.`}
            />
          )}
        </Box>
      )}
    </Stack>
  );
}
